import { useCallback, useEffect, useMemo, useState } from 'react'
import { Car, ShieldAlert, AlertCircle, AlertTriangle, Lock, History, UserX } from 'lucide-react'
import {
  useGetAllocationCurrent,
  useGetAllocationSeatChanges,
  useGetAllocationIneligibleSeated,
  useGetEligibleDriversForAllocation,
  useGetAllocationBench,
  useGetVehicleManagerBook,
} from '../hooks/backend/fleet'
import { useGetDriverManagerBoard, useGetAddressHealth } from '../hooks/backend/drivers'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { cn } from '../lib/shadcn/utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../lib/shadcn/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../lib/shadcn/tooltip'
import { Chip } from '../components/interviews/Chip'
import { SwapVehiclesDialog } from '../components/fleet/SwapVehiclesDialog'
import { ManualChangeSeatDialog } from '../components/fleet/ManualChangeSeatDialog'
import { AllocateDriverDialog } from '../components/fleet/AllocateDriverDialog'
import { BenchedDriversPanel } from '../components/fleet/BenchedDriversPanel'
import { ReallocateButton } from '../components/fleet/ReallocateButton'
import { ManagerCell, type ManagerState } from '../components/managers/ManagerCell'
import { VehicleManagerControl } from '../components/managers/VehicleManagerControl'
import { ManagerBalancePanel } from '../components/managers/ManagerBalancePanel'
import { AllocationMapView } from '../components/fleet/AllocationMapView'
import {
  type AllocationSeat,
  type AllocationSeatChange,
  type IneligibleSeated,
  type EligibleDriver,
  type BenchDriver,
  type VehicleOption,
  groupByVehicle,
  humanize,
  formatFullDate,
  formatDateTime,
} from '../utils/allocation'
import type { VehicleBookRow, DriverBoardRow } from '../utils/managers'
import type { AddressHealthRow } from '../utils/addressHealth'

// Two views under one tab, and one nav entry, the same shape Performance uses
// for Fleet and Operators. Grid is everything this screen already did. Map
// answers a different question with the same allocation: where the men on each
// car live, and how far apart the handover actually is.
type View = 'grid' | 'map'

export default function VehicleAllocation() {
  const { loading: accessLoading, canView, canEditFleet, canEditDrivers, isAdmin, caption } = useCapabilities()
  const canEdit = canEditFleet || canEditDrivers || isAdmin
  const [view, setView] = useState<View>('grid')

  const allocation = useGetAllocationCurrent()
  const changes = useGetAllocationSeatChanges()
  const ineligible = useGetAllocationIneligibleSeated()
  const eligible = useGetEligibleDriversForAllocation()
  const bench = useGetAllocationBench()
  const vehicleBook = useGetVehicleManagerBook()
  const driverBoard = useGetDriverManagerBoard()
  // Coordinates for the map. Deliberately reuses the address health read rather
  // than adding an endpoint: it already returns lat and lng for every driver,
  // already requires canView, and adding nothing to api/src/handlers keeps the
  // parity proof in section 4 of CLAUDE.md untouched. onlyNeedsAttention is
  // false here because the map wants the clean addresses, which is the opposite
  // of what the health screen wants.
  const addressHealth = useGetAddressHealth()
  const allocTrigger = allocation.trigger
  const changesTrigger = changes.trigger
  const ineligibleTrigger = ineligible.trigger
  const eligibleTrigger = eligible.trigger
  const benchTrigger = bench.trigger
  const vehicleBookTrigger = vehicleBook.trigger
  const driverBoardTrigger = driverBoard.trigger
  const addressHealthTrigger = addressHealth.trigger

  const load = useCallback(() => {
    allocTrigger({}, { skipCache: true })
    changesTrigger({}, { skipCache: true })
    ineligibleTrigger({}, { skipCache: true })
    eligibleTrigger({}, { skipCache: true })
    benchTrigger({}, { skipCache: true })
    vehicleBookTrigger({}, { skipCache: true })
    // 'all' rather than 'active': a seat can hold a driver whose status has since
    // changed, and a blank manager cell on such a seat would read as a bug.
    driverBoardTrigger({ status: 'all' }, { skipCache: true })
  }, [
    allocTrigger,
    changesTrigger,
    ineligibleTrigger,
    eligibleTrigger,
    benchTrigger,
    vehicleBookTrigger,
    driverBoardTrigger,
  ])

  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  // Addresses are fetched only once the map is opened. The grid does not use
  // them, and this read returns every driver's home address, so it should not
  // travel on a page view that has no use for it.
  useEffect(() => {
    if (canView && view === 'map') addressHealthTrigger({ onlyNeedsAttention: false }, { skipCache: true })
  }, [canView, view, addressHealthTrigger])

  const seatRows = (allocation.data as AllocationSeat[] | undefined) ?? []
  const changeRows = (changes.data as AllocationSeatChange[] | undefined) ?? []
  const ineligibleRows = (ineligible.data as IneligibleSeated[] | undefined) ?? []
  const eligibleDrivers = (eligible.data as EligibleDriver[] | undefined) ?? []
  const benchDrivers = (bench.data as BenchDriver[] | undefined) ?? []
  const addressRows = (addressHealth.data as AddressHealthRow[] | undefined) ?? []

  const vehicleGroups = useMemo(() => groupByVehicle(seatRows), [seatRows])

  // Who manages each car, and who manages each driver. Kept as two maps rather
  // than read off v_driver_manager_effective's manager_state, because that view
  // resolves the seat from the latest PUBLISHED run while this grid shows the
  // latest run of any status. Comparing against the car actually on screen is
  // the only comparison an operator can act on.
  const vehicleManagerById = useMemo(() => {
    const rows = (vehicleBook.data as VehicleBookRow[] | undefined) ?? []
    return new Map(rows.map((r) => [r.vehicle_id, r]))
  }, [vehicleBook.data])

  const driverManagerById = useMemo(() => {
    const rows = (driverBoard.data as DriverBoardRow[] | undefined) ?? []
    return new Map(rows.map((r) => [r.driver_id, r]))
  }, [driverBoard.data])

  function managerStateFor(driverId: string, vehicleManagerPersonId: string | null): ManagerState {
    const driverManagerPersonId = driverManagerById.get(driverId)?.manager_person_id ?? null
    if (!driverManagerPersonId) return 'unassigned'
    if (!vehicleManagerPersonId) return 'car_unassigned'
    return driverManagerPersonId === vehicleManagerPersonId ? 'ok' : 'mismatch'
  }

  // Every card can swap with any other vehicle in the run.
  const vehicleOptions = useMemo<VehicleOption[]>(
    () =>
      vehicleGroups.map((g) => ({
        vehicle_id: g.vehicle_id,
        plate: g.plate,
        make: g.make,
        model: g.model,
      })),
    [vehicleGroups],
  )

  const meta = seatRows[0]

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access...
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            Vehicle Allocation is available to admins only.
          </p>
          <p className="text-xs text-muted-foreground">
            Contact an admin if you need access to allocation data.
          </p>
        </div>
      </div>
    )
  }

  const loading = allocation.loading || changes.loading
  const anyError = allocation.error || changes.error

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      {/* Header */}
      <div className="border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <Car className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Vehicle Allocation</h1>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-muted-foreground tabular-nums">
              {loading ? '...' : `${vehicleGroups.length} vehicle${vehicleGroups.length === 1 ? '' : 's'}`}
            </span>
            <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
              {(['grid', 'map'] as View[]).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  className={`rounded px-3 py-1 text-xs font-medium capitalize transition-colors ${
                    view === v
                      ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                      : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
            {canEdit ? <ReallocateButton onReallocated={load} /> : null}
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {anyError ? (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{anyError}</span>
          </div>
        ) : loading && seatRows.length === 0 ? (
          <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading allocation...
          </div>
        ) : view === 'map' ? (
          <AllocationMapView
            seats={seatRows}
            addresses={addressRows}
            bench={benchDrivers}
            driverBoard={(driverBoard.data as DriverBoardRow[] | undefined) ?? []}
            loading={allocation.loading || addressHealth.loading}
          />
        ) : (
          <div className="space-y-6">
            {/* Draft warning banner */}
            {meta && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="space-y-0.5">
                  <p className="font-medium">
                    Week starting {formatFullDate(meta.week_starting)}, run status{' '}
                    {humanize(meta.run_status)}.
                  </p>
                  <p className="text-xs">
                    Nothing is published yet. This grid shows only the most recent run, and there are
                    several draft runs for this week. Treat these seats as provisional until a run is
                    published.
                  </p>
                </div>
              </div>
            )}

            {/* Benched drivers: a decision with a pay consequence belongs on the
                screen where it was made, not only in the audit table below. */}
            <BenchedDriversPanel bench={benchDrivers} canEdit={canEdit} onChanged={load} />

            {/* Ineligible seated drivers banner: only when there is at least one */}
            {ineligibleRows.length > 0 && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                <div className="flex items-center gap-2 font-medium">
                  <UserX className="h-4 w-4 shrink-0" />
                  {ineligibleRows.length} seated driver{ineligibleRows.length === 1 ? ' is' : 's are'} no
                  longer eligible
                </div>
                <ul className="mt-2 space-y-1">
                  {ineligibleRows.map((r) => (
                    <li key={r.seat_id} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                      <span className="font-mono font-semibold">{r.registration_number || 'Unknown car'}</span>
                      <span>Seat {r.shift_index}</span>
                      <span className="font-medium">{r.driver_name || 'Unnamed driver'}</span>
                      <span className="text-destructive/80">{r.block_reason || 'No longer eligible.'}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Manager books. Balance is judged on cars that are on the road. */}
            <ManagerBalancePanel />

            {/* Allocation grid grouped by vehicle */}
            <section className="space-y-3">
              {vehicleGroups.length === 0 ? (
                <p className="text-sm text-muted-foreground">No vehicles in the current run.</p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {vehicleGroups.map((g) => {
                    const others = vehicleOptions.filter((v) => v.vehicle_id !== g.vehicle_id)
                    const carBook = vehicleManagerById.get(g.vehicle_id)
                    const carManagerPersonId = carBook?.manager_person_id ?? null
                    return (
                      <div key={g.vehicle_id} className="rounded-lg border border-border bg-background p-4">
                        <div className="mb-3 flex items-start gap-2">
                          <Car className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--brand))]" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-mono text-sm font-semibold text-foreground">
                              {g.plate}
                            </p>
                            <p className="truncate text-xs text-muted-foreground">
                              {[g.make, g.model].filter(Boolean).join(' ') || 'Unknown model'}
                            </p>
                            <div className="mt-1.5">
                              <Chip tone="neutral">{humanize(g.operational_state)}</Chip>
                            </div>
                          </div>
                        </div>

                        {/* The CAR's manager. Deliberately labelled and placed on
                            the card header, away from the driver controls on the
                            seats below, so nobody moves a car meaning to move a
                            person. Changing it moves no driver: each affected seat
                            raises a mismatch badge for a human to accept. */}
                        <div className="mb-3 border-b border-border pb-3">
                          <VehicleManagerControl
                            vehicleId={g.vehicle_id}
                            plate={g.plate}
                            managerPersonId={carManagerPersonId}
                            operational={carBook?.operational ?? undefined}
                            canEdit={canEditFleet || isAdmin}
                            onChanged={load}
                          />
                        </div>

                        {/* Card actions: manual allocation next to swap car */}
                        {canEdit ? (
                          <div className="mb-3 flex items-center gap-2">
                            <AllocateDriverDialog
                              vehicle={g}
                              benchDrivers={benchDrivers}
                              seatedDrivers={eligibleDrivers}
                              onAllocated={load}
                            />
                            {g.hasLockedSeat ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    {/* A plain span wrapper keeps the tooltip working on a disabled control. */}
                                    <span className="block w-full">
                                      <button
                                        type="button"
                                        disabled
                                        className="flex h-7 w-full cursor-not-allowed items-center justify-center gap-1.5 rounded-md border border-border bg-background px-2 text-xs font-medium text-muted-foreground opacity-60"
                                      >
                                        <Lock className="h-3.5 w-3.5" />
                                        Swap car
                                      </button>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    This vehicle has a locked seat, so its crew cannot be swapped. Unlock
                                    the seat first.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <SwapVehiclesDialog
                                vehicleA={g}
                                otherVehicles={others}
                                onSwapped={load}
                              />
                            )}
                          </div>
                        ) : null}

                        {/* Seats 1, 2, 3 */}
                        <ul className="space-y-2">
                          {g.seats.map((s) => {
                            const filled = !s.is_empty_slot && s.seat_id != null
                            // A planned seat is a manager's placement Uber has not confirmed.
                            // It carries the brand colour, the same treatment as the chosen
                            // tab in the sidebar, so it reads as different at a glance.
                            const planned = filled && s.is_planned === true
                            return (
                              <li
                                key={s.seat_id ?? `${g.vehicle_id}-${s.shift_index}`}
                                className={cn(
                                  'flex items-start gap-2 rounded-md border p-2.5 transition-colors',
                                  planned
                                    ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] ring-1 ring-inset ring-[hsl(var(--brand))]'
                                    : 'border-border bg-card',
                                )}
                              >
                                <span
                                  className={cn(
                                    'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                                    planned
                                      ? 'bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]'
                                      : 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]',
                                  )}
                                >
                                  {s.shift_index}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5">
                                    {planned && (
                                      <span className="rounded-sm bg-[hsl(var(--brand))] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[hsl(var(--brand-foreground))]">
                                        Planned
                                      </span>
                                    )}
                                    <span
                                      className={cn(
                                        'text-[11px] font-medium uppercase tracking-wide',
                                        planned
                                          ? 'text-[hsl(var(--brand-soft-foreground))]'
                                          : 'text-muted-foreground',
                                      )}
                                    >
                                      {s.seat_label}
                                    </span>
                                    {s.locked && (
                                      <Lock
                                        className="h-3 w-3 text-muted-foreground"
                                        aria-label="Locked seat"
                                      />
                                    )}
                                  </div>
                                  <p
                                    className={cn(
                                      'truncate text-sm',
                                      planned
                                        ? 'font-semibold text-[hsl(var(--brand-soft-foreground))]'
                                        : filled
                                          ? 'font-medium text-foreground'
                                          : 'italic text-muted-foreground',
                                    )}
                                  >
                                    {filled ? s.driver_name || 'Unnamed driver' : 'Empty'}
                                  </p>
                                  {planned && s.plan_note ? (
                                    <p className="mt-0.5 text-[11px] leading-snug text-[hsl(var(--brand-soft-foreground))]">
                                      {s.plan_note}
                                    </p>
                                  ) : null}
                                  {filled && s.driver_id ? (
                                    <div className="mt-1.5">
                                      <ManagerCell
                                        driverId={s.driver_id}
                                        driverName={s.driver_name || 'this driver'}
                                        managerPersonId={
                                          driverManagerById.get(s.driver_id)?.manager_person_id ?? null
                                        }
                                        managerState={managerStateFor(s.driver_id, carManagerPersonId)}
                                        plate={g.plate}
                                        vehicleManagerPersonId={carManagerPersonId}
                                        vehicleManagerName={carBook?.manager_name ?? null}
                                        canEdit={canEditDrivers || isAdmin}
                                        requireReason
                                        onChanged={load}
                                      />
                                    </div>
                                  ) : null}
                                  {canEdit && filled ? (
                                    <div className="mt-1.5">
                                      <ManualChangeSeatDialog
                                        seat={s}
                                        vehicles={vehicleOptions}
                                        onMoved={load}
                                      />
                                    </div>
                                  ) : null}
                                </div>
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    )
                  })}
                </div>
              )}
            </section>

            {/* Audit trail */}
            <section>
              <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <History className="h-4 w-4 text-muted-foreground" />
                Change history
              </h2>
              {changeRows.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No allocation changes have been recorded yet.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Change</TableHead>
                        <TableHead>Vehicle A</TableHead>
                        <TableHead>Vehicle B</TableHead>
                        <TableHead>Driver</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>When</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {changeRows.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell>
                            <Chip tone="neutral">{humanize(c.change_type)}</Chip>
                          </TableCell>
                          <TableCell className="font-mono text-xs text-foreground">
                            {c.vehicle_a_plate || (c.vehicle_a ? c.vehicle_a.slice(0, 8) : '-')}
                          </TableCell>
                          <TableCell className="font-mono text-xs text-foreground">
                            {c.vehicle_b_plate || (c.vehicle_b ? c.vehicle_b.slice(0, 8) : '-')}
                          </TableCell>
                          <TableCell className="text-sm text-foreground">
                            {c.driver_name || (c.driver_id ? c.driver_id.slice(0, 8) : '-')}
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <span className="block truncate text-sm text-muted-foreground" title={c.reason ?? ''}>
                              {c.reason || '-'}
                            </span>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                            {formatDateTime(c.changed_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  )
}
